import type { ITiledMap, ITiledMapLayer, ITiledMapTileLayer } from "@workadventure/tiled-map-type-guard";
import type { TileChange, TileLayerEdit, TileMapOverlay, WAMFileFormat } from "../types";

/**
 * Pure, Phaser-free helpers to apply/undo tile edits on an ITiledMap (TMJ) in memory.
 *
 * This class is the single source of truth for how a tile/layer edit mutates the map model,
 * so it can be used both at load time (to apply the persisted overlay) and at edit time
 * (to apply a command locally before broadcasting it).
 */
export class TilemapEditsManager {
    /**
     * Returns (creating if necessary) the tile map overlay stored inside a WAM file.
     */
    public static getOrCreateOverlay(wam: WAMFileFormat): TileMapOverlay {
        if (!wam.tileMapOverlay) {
            wam.tileMapOverlay = { tileChanges: [], layerEdits: [] };
        }
        return wam.tileMapOverlay;
    }

    public static findTileLayer(map: ITiledMap, layerId: number): ITiledMapTileLayer | undefined {
        return TilemapEditsManager.getAllTileLayers(map).find((layer) => layer.id === layerId);
    }

    /**
     * Recursively collects every tile layer of the map along with the array containing it,
     * in flattened order (layers nested inside group layers included).
     * The returned layers are the actual layer objects (not copies), so mutating them mutates the map.
     */
    private static getTileLayerPlacements(
        map: ITiledMap,
    ): Array<{ parent: ITiledMapLayer[]; index: number; layer: ITiledMapTileLayer }> {
        const placements: Array<{ parent: ITiledMapLayer[]; index: number; layer: ITiledMapTileLayer }> = [];
        const visit = (layers: ITiledMapLayer[]): void => {
            layers.forEach((layer, index) => {
                if (layer.type === "tilelayer") {
                    placements.push({ parent: layers, index, layer });
                } else if (layer.type === "group") {
                    visit(layer.layers);
                }
            });
        };
        visit(map.layers);
        return placements;
    }

    private static getAllTileLayers(map: ITiledMap): ITiledMapTileLayer[] {
        return TilemapEditsManager.getTileLayerPlacements(map).map((placement) => placement.layer);
    }

    /**
     * Returns a layer id not used by any layer of the map (max existing id + 1).
     * Scans every layer recursively, whatever its type (Tiled ids are unique across all layers).
     */
    public static generateLayerId(map: ITiledMap): number {
        let maxId = 0;
        const visit = (layers: ITiledMapLayer[]): void => {
            for (const layer of layers) {
                if (typeof layer.id === "number" && layer.id > maxId) {
                    maxId = layer.id;
                }
                if (layer.type === "group") {
                    visit(layer.layers);
                }
            }
        };
        visit(map.layers);
        return maxId + 1;
    }

    /**
     * Returns the tile gid at the given cell, or 0 (empty) when the cell/layer does not exist
     * or the layer data is not editable (encoded as a string).
     */
    public static getTileAt(map: ITiledMap, layerId: number, x: number, y: number): number {
        const layer = TilemapEditsManager.findTileLayer(map, layerId);
        if (!layer || typeof layer.data === "string") {
            return 0;
        }
        return layer.data[x + y * layer.width] ?? 0;
    }

    /**
     * Sets a single tile and returns the corresponding TileChange (oldTile -> newTile).
     * Returns undefined when the layer does not exist, is not editable, or the cell is already set.
     */
    public static putTile(
        map: ITiledMap,
        layerId: number,
        x: number,
        y: number,
        newTile: number,
    ): TileChange | undefined {
        const layer = TilemapEditsManager.findTileLayer(map, layerId);
        if (!layer || typeof layer.data === "string") {
            return undefined;
        }
        const index = x + y * layer.width;
        const oldTile = layer.data[index] ?? 0;
        if (oldTile === newTile) {
            return undefined;
        }
        layer.data[index] = newTile;
        return { layerId, x, y, oldTile, newTile };
    }

    /**
     * Applies a list of tile changes (used to replay the persisted overlay on top of the TMJ).
     * Changes whose coordinates fall outside the layer bounds are ignored (they can be leftovers
     * from a previous resize that shrunk the map).
     */
    public static applyTileChanges(map: ITiledMap, changes: TileChange[]): void {
        for (const change of changes) {
            const layer = TilemapEditsManager.findTileLayer(map, change.layerId);
            if (!layer || typeof layer.data === "string") {
                continue;
            }
            if (change.x < 0 || change.y < 0 || change.x >= layer.width || change.y >= layer.height) {
                continue;
            }
            const index = change.x + change.y * layer.width;
            layer.data[index] = change.newTile;
        }
    }

    /**
     * Returns the inverse of a list of tile changes (for undo).
     */
    public static invertTileChanges(changes: TileChange[]): TileChange[] {
        return changes.map((change) => ({ ...change, oldTile: change.newTile, newTile: change.oldTile }));
    }

    /**
     * Flood-fills the region of same tile (target = tile at x,y) with newTile.
     * Only spreads through cells matching the initial tile value.
     */
    public static floodFill(map: ITiledMap, layerId: number, x: number, y: number, newTile: number): TileChange[] {
        const layer = TilemapEditsManager.findTileLayer(map, layerId);
        if (!layer || typeof layer.data === "string") {
            return [];
        }
        const width = layer.width;
        const height = layer.height;
        const data = layer.data;
        const target = data[x + y * width] ?? 0;
        if (target === newTile) {
            return [];
        }

        const changes: TileChange[] = [];
        const visited = new Set<number>();
        const stack: Array<[number, number]> = [[x, y]];

        while (stack.length > 0) {
            const [cx, cy] = stack.pop() as [number, number];
            if (cx < 0 || cy < 0 || cx >= width || cy >= height) {
                continue;
            }
            const index = cx + cy * width;
            if (visited.has(index)) {
                continue;
            }
            visited.add(index);
            if ((data[index] ?? 0) !== target) {
                continue;
            }
            data[index] = newTile;
            changes.push({ layerId, x: cx, y: cy, oldTile: target, newTile });
            stack.push([cx + 1, cy], [cx - 1, cy], [cx, cy + 1], [cx, cy - 1]);
        }

        return changes;
    }

    /**
     * Fills the rectangular region between (x0,y0) and (x1,y1), inclusive, with newTile.
     * Only cells whose current value differs from newTile are changed (and returned).
     * Coordinates outside the layer bounds are clamped.
     */
    public static fillRectangle(
        map: ITiledMap,
        layerId: number,
        x0: number,
        y0: number,
        x1: number,
        y1: number,
        newTile: number,
    ): TileChange[] {
        const layer = TilemapEditsManager.findTileLayer(map, layerId);
        if (!layer || typeof layer.data === "string") {
            return [];
        }
        const minX = Math.max(0, Math.min(x0, x1));
        const maxX = Math.min(layer.width - 1, Math.max(x0, x1));
        const minY = Math.max(0, Math.min(y0, y1));
        const maxY = Math.min(layer.height - 1, Math.max(y0, y1));

        const changes: TileChange[] = [];
        for (let y = minY; y <= maxY; y++) {
            for (let x = minX; x <= maxX; x++) {
                const index = x + y * layer.width;
                const oldTile = layer.data[index] ?? 0;
                if (oldTile === newTile) {
                    continue;
                }
                layer.data[index] = newTile;
                changes.push({ layerId, x, y, oldTile, newTile });
            }
        }
        return changes;
    }

    /**
     * Resizes the map (and every editable tile layer) preserving the top-left content.
     * Content outside the new bounds is dropped; new cells are filled with 0 (empty).
     *
     * The layer `data` arrays are mutated in place (not replaced) so that any other reference
     * to them (e.g. the flattened layers used by the frontend) stays in sync.
     */
    public static resize(map: ITiledMap, newWidth: number, newHeight: number): void {
        const oldWidth = map.width ?? 0;
        const oldHeight = map.height ?? 0;

        for (const layer of TilemapEditsManager.getAllTileLayers(map)) {
            if (typeof layer.data === "string") {
                continue;
            }
            const copyWidth = Math.min(oldWidth, newWidth);
            const copyHeight = Math.min(oldHeight, newHeight);
            const newData = new Array<number>(newWidth * newHeight).fill(0);
            for (let row = 0; row < copyHeight; row++) {
                for (let col = 0; col < copyWidth; col++) {
                    newData[col + row * newWidth] = layer.data[col + row * oldWidth] ?? 0;
                }
            }

            // Mutate the existing array in place to preserve shared references.
            layer.data.length = 0;
            for (const value of newData) {
                layer.data.push(value);
            }
            layer.width = newWidth;
            layer.height = newHeight;
        }

        map.width = newWidth;
        map.height = newHeight;
    }

    /**
     * Creates an empty tile layer sized to the map and inserts it at the given flat index
     * (position among all tile layers, in flattened order). If the target position is held by
     * a layer nested in a group, the new layer is inserted in that same group. A flat index
     * past the last tile layer appends the layer at the top level.
     */
    public static createTileLayer(
        map: ITiledMap,
        config: { layerId: number; name: string; index: number; opacity: number; visible: boolean },
    ): ITiledMapTileLayer {
        const width = map.width ?? 0;
        const height = map.height ?? 0;
        const layer: ITiledMapTileLayer = {
            id: config.layerId,
            name: config.name,
            type: "tilelayer",
            width,
            height,
            opacity: config.opacity,
            visible: config.visible,
            data: new Array<number>(width * height).fill(0),
        };
        const placements = TilemapEditsManager.getTileLayerPlacements(map);
        if (config.index < placements.length) {
            const { parent, index } = placements[config.index];
            parent.splice(index, 0, layer);
        } else {
            map.layers.push(layer);
        }
        return layer;
    }

    public static deleteTileLayer(map: ITiledMap, layerId: number): boolean {
        const placement = TilemapEditsManager.getTileLayerPlacements(map).find((entry) => entry.layer.id === layerId);
        if (!placement) {
            return false;
        }
        placement.parent.splice(placement.index, 1);
        return true;
    }

    /**
     * Moves a tile layer to the given flat index (position among all tile layers, in flattened
     * order). The layer is removed first, then the flat index is resolved against the updated
     * order: if the target position is held by a layer nested in a group, the moved layer is
     * inserted in that same group. A flat index past the last tile layer moves it to the top level.
     */
    public static moveTileLayer(map: ITiledMap, layerId: number, newIndex: number): boolean {
        const placement = TilemapEditsManager.getTileLayerPlacements(map).find((entry) => entry.layer.id === layerId);
        if (!placement) {
            return false;
        }
        placement.parent.splice(placement.index, 1);
        const updatedPlacements = TilemapEditsManager.getTileLayerPlacements(map);
        if (newIndex < updatedPlacements.length) {
            const { parent, index } = updatedPlacements[newIndex];
            parent.splice(index, 0, placement.layer);
        } else {
            map.layers.push(placement.layer);
        }
        return true;
    }

    public static updateTileLayer(
        map: ITiledMap,
        layerId: number,
        patch: { name?: string; opacity?: number; visible?: boolean },
    ): boolean {
        const layer = TilemapEditsManager.findTileLayer(map, layerId);
        if (!layer) {
            return false;
        }
        if (patch.name !== undefined) {
            layer.name = patch.name;
        }
        if (patch.opacity !== undefined) {
            layer.opacity = patch.opacity;
        }
        if (patch.visible !== undefined) {
            layer.visible = patch.visible;
        }
        return true;
    }

    /**
     * Applies a persisted overlay on top of the base TMJ, in order:
     * resize, layer edits, then tile changes.
     */
    public static applyOverlay(map: ITiledMap, overlay: TileMapOverlay): void {
        if (overlay.mapWidth !== undefined || overlay.mapHeight !== undefined) {
            TilemapEditsManager.resize(map, overlay.mapWidth ?? map.width ?? 0, overlay.mapHeight ?? map.height ?? 0);
        }

        for (const edit of overlay.layerEdits) {
            TilemapEditsManager.applyLayerEdit(map, edit);
        }

        TilemapEditsManager.applyTileChanges(map, overlay.tileChanges);
    }

    public static applyLayerEdit(map: ITiledMap, edit: TileLayerEdit): void {
        switch (edit.action) {
            case "create":
                TilemapEditsManager.createTileLayer(map, edit);
                break;
            case "delete":
                TilemapEditsManager.deleteTileLayer(map, edit.layerId);
                break;
            case "move":
                TilemapEditsManager.moveTileLayer(map, edit.layerId, edit.newIndex);
                break;
            case "update":
                TilemapEditsManager.updateTileLayer(map, edit.layerId, edit);
                break;
        }
    }
}
